/**
 * @fileoverview Main application component for Ghost Cvlt website.
 * Features smooth scroll integration, animated preloader, and responsive design.
 */

import Preloader from './components/preloader';
import { useState } from 'react';
import Navigation from './components/navigation';
import Character from './components/OpacityGradientTextByCharacter';
import Marquee from './components/InfiniteTextMarquee';
import { motion, Transition, Variants } from 'motion/react';
import { twMerge } from 'tailwind-merge';
import '@/src/App.css';
import '@/src/styles/accessibility.css';
import Footer from './components/footer/footerWrapper';
import { useLenis } from './hooks/useLenis';
import type { ProjectCardProps, ServicesProps } from './types/components';

/**
 * Project card component displaying portfolio work with media background.
 *
 * @description Renders a project card with video or image background, project details,
 * and hover effects. Includes responsive design and accessibility features.
 *
 * @param {ProjectCardProps} props - Project information and media assets
 * @returns {JSX.Element} Project card component
 */
const ProjectCard = ({
  project_title,
  main_service,
  client_name,
  release_year,
  background_asset,
}: ProjectCardProps) => {
  /** Determine if background asset is a video file */
  const isVideo = background_asset.match(/\.(mp4|webm|ogg|mov)$/i);

  return (
    <article className="relative w-full cursor-pointer aspect-[100/98] group overflow-hidden">
      {/* Background media asset - video or image */}
      {isVideo ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        >
          <source src={background_asset} type="video/mp4" />
        </video>
      ) : (
        <img
          src={background_asset}
          alt={`Proyecto ${project_title} - ${main_service}`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      )}

      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-transparent" />

      {/* Project text content - top left */}
      <header className="absolute top-0 left-0 p-4">
        <div className="flex flex-col text-white text-lg/tight font-urbanist font-black tracking-tight uppercase">
          <h3 className="m-0 p-0">{project_title}</h3>
          <p className="m-0 p-0">{main_service}</p>
          <p className="m-0 p-0">{client_name}</p>
          <time className="m-0 p-0" dateTime={release_year}>
            {release_year}
          </time>
        </div>
      </header>
    </article>
  );
};

const Service = ({
  title1,
  title2,
  src,
  textClassName,
  firstTitleClassName,
  secondTitleClassName,
  wrapperClassName,
  imgWrapperClassName,
}: ServicesProps) => {
  const [isActive, setIsActive] = useState(false);

  // Animaciones para el texto (igual que NavLink)
  const textVariants = {
    initial: { y: '0%', opacity: 1 },
    hover: { y: '-100%', opacity: 0 },
  };

  const textBottomVariants = {
    initial: { y: '100%', opacity: 0 },
    hover: { y: '0%', opacity: 1 },
  };

  const textTransition: Transition = {
    duration: 0.12,
    ease: 'linear',
  };

  // Animación para las imágenes laterales
  const imageVariants: Variants = {
    initial: { opacity: 0, scale: 0.8 },
    hover: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] },
    },
  };

  return (
    <article
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      className={twMerge(
        `border-t-2 border-black py-[0.8vw] cursor-pointer flex justify-center items-center last-of-type:border-b-2 last-of-type:border-black`,
        wrapperClassName,
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsActive(!isActive);
        }
      }}
      aria-label={`Servicio: ${title1} ${title2}`}
    >
      {/* Imagen izquierda */}
      <motion.div
        variants={imageVariants}
        initial="initial"
        animate={isActive ? 'hover' : 'initial'}
        className="hidden lg:flex justify-center items-center mr-8 lg:mr-12"
      >
        <img
          src={src}
          alt={`${title1} ${title2}`}
          className={twMerge(
            'w-auto h-[8vh] aspect-video object-cover',
            imgWrapperClassName,
          )}
        />
      </motion.div>

      {/* Contenedor del título completo */}
      <div className="flex items-baseline gap-1 lg:gap-4">
        {/* Primer texto (title1) */}
        <div className="relative overflow-hidden">
          <motion.p
            className={twMerge(
              `text-nowrap m-0 inline-block`,
              textClassName,
              firstTitleClassName,
            )}
            variants={textVariants}
            initial="initial"
            animate={isActive ? 'hover' : 'initial'}
            transition={textTransition}
          >
            {title1}
          </motion.p>
          <motion.p
            className={twMerge(
              `absolute left-0 top-0 text-nowrap m-0 inline-block`,
              textClassName,
              firstTitleClassName,
            )}
            variants={textBottomVariants}
            initial="initial"
            animate={isActive ? 'hover' : 'initial'}
            transition={textTransition}
          >
            {title1}
          </motion.p>
        </div>

        {/* Segundo texto (title2) */}
        <div className="relative overflow-hidden">
          <motion.p
            className={twMerge(
              `text-nowrap m-0 inline-block`,
              textClassName,
              secondTitleClassName,
            )}
            variants={textVariants}
            initial="initial"
            animate={isActive ? 'hover' : 'initial'}
            transition={textTransition}
          >
            {title2}
          </motion.p>
          <motion.p
            className={twMerge(
              `absolute left-0 top-0 text-nowrap m-0 inline-block`,
              textClassName,
              secondTitleClassName,
            )}
            variants={textBottomVariants}
            initial="initial"
            animate={isActive ? 'hover' : 'initial'}
            transition={textTransition}
          >
            {title2}
          </motion.p>
        </div>
      </div>

      {/* Imagen derecha */}
      <motion.div
        variants={imageVariants}
        initial="initial"
        animate={isActive ? 'hover' : 'initial'}
        className="hidden lg:flex justify-center items-center ml-8 lg:ml-12"
      >
        <img
          src={src}
          alt={`${title1} ${title2}`}
          className={twMerge(
            'w-auto h-[8vh] aspect-video object-cover',
            imgWrapperClassName,
          )}
        />
      </motion.div>
    </article>
  );
};

const App = () => {
  const [showContent, setShowContent] = useState(false);

  // Inicializar Lenis para scroll suave profesional
  useLenis();

  const handlePreloaderComplete = () => {
    setShowContent(true);
  };

  const services: ServicesProps[] = [
    {
      title1: 'Diseño',
      title2: 'Gráfico',
      src: '/services_images/diseno_grafico.jpg',
    },
    {
      title1: 'Producción',
      title2: 'Audiovisual',
      src: '/services_images/produccion_audiovisual.jpg',
    },
    // {
    //   title1: 'Producción',
    //   title2: 'Sonora',
    //   src: '/services_images/produccion_sonora.jpg',
    // },
    {
      title1: 'Producción',
      title2: 'de Eventos',
      src: '/services_images/produccion_eventos.jpg',
    },
    {
      title1: 'Desarrollo',
      title2: 'Web',
      src: '/services_images/diseno_web.jpg',
    },
  ];

  const projects: ProjectCardProps[] = [
    {
      project_title: 'Y me duele',
      client_name: 'Turistas',
      release_year: '2025',
      main_service: 'Producción Audiovisual',
      background_asset: '/projects/y_me_duele/img2.jpg',
    },
    {
      project_title: 'Viejito',
      client_name: 'Ghost Cvlt Films',
      release_year: '2025',
      main_service: 'Cortometraje',
      background_asset: '/projects/viejito/img1.jpg',
    },
  ];

  return (
    <div className="relative" data-lenis-root>
      {/* Preloader se ejecuta cuando los assets están cargados */}
      <Preloader onComplete={handlePreloaderComplete} minDuration={1000} />

      {showContent && (
        <main id="main-content">
          <Navigation />
          <header
            className="relative h-screen max-h-screen overflow-hidden"
            role="banner"
          >
            <video
              controls={false}
              autoPlay
              muted
              loop
              playsInline
              className="absolute top-0 left-0 w-full h-full object-cover z-0"
              preload="false"
            >
              <source src="/background_hero.webm" type="video/webm" />
            </video>

            {/* Contenido del Hero */}
            <section className="relative z-20 flex flex-col items-center justify-center h-full text-white w-screen text-center mix-blend-difference">
              <h1 className="text-[2.5rem] lg:text-[5.6rem] leading-none  font-bold uppercase tracking-tight px-6 lg:px-0 lg:w-2/3 text-shadow-lg">
                Somos un colectivo creativo generando <br />
                <span className="text-[2.5rem] lg:text-[6rem] font-ibm-plex-serif font-normal italic">
                  comunidad
                </span>{' '}
                y{' '}
                <span className="text-[2.5rem] lg:text-[6rem] font-ibm-plex-serif font-normal italic">
                  cultura
                </span>
              </h1>
            </section>

            <div className="absolute bottom-0 left-0 right-0 grid grid-cols-2 lg:grid-cols-3 items-center px-6 lg:px-9 py-3 w-full z-20 mix-blend-difference">
              {/* EST. 2017 - coincide con logo position */}
              <div className="justify-self-start">
                <p className="font-urbanist uppercase font-black text-lg tracking-tight text-white">
                  EST. 2017
                  <br />
                  En CDMX
                </p>
              </div>

              {/* Espacio central vacío (coincide con links desktop) */}
              <div className="hidden lg:block"></div>

              {/* Información adicional opcional (coincide con contacto) */}
              <div className="hidden lg:block justify-self-end">
                <p className="font-urbanist uppercase font-black text-lg tracking-tight text-white">
                  © Ghost Cvlt
                </p>
              </div>
            </div>
          </header>

          {/* Company Short Desc */}
          <section
            className="h-full lg:h-[70vh] flex items-center justify-center w-full lg:w-4/5 mx-auto leading-none"
            id="nosotros"
            aria-labelledby="about-heading"
          >
            <h2 id="about-heading" className="sr-only">
              Acerca de nosotros
            </h2>
            <Character
              paragraph="Somos un colectivo creativo con sede en la Ciudad de México, dedicado a generar comunidad y cultura a través de proyectos innovadores y colaborativos."
              className="text-[6vw] lg:text-6xl font-urbanist font-black uppercase text-white px-9 text-center"
            />
          </section>

          {/* Projects */}
          <section id="proyectos" aria-labelledby="projects-heading">
            <h2 id="projects-heading" className="sr-only">
              Nuestros proyectos
            </h2>
            <header className="py-6">
              <Marquee
                text="TRABAJOS SELECCIONADOS"
                className="text-white text-2xl lg:text-5xl font-urbanist font-black uppercase tracking-tight text-center"
                duration={350}
                separatorSpacing="medium"
              />
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {projects.map((project) => (
                <ProjectCard
                  key={project.project_title}
                  project_title={project.project_title}
                  client_name={project.client_name}
                  release_year={project.release_year}
                  main_service={project.main_service}
                  background_asset={project.background_asset}
                />
              ))}
            </div>
          </section>

          {/* Sección de Servicios */}
          <section id="servicios" aria-labelledby="services-heading">
            {/* Header */}
            <header className="h-fit lg:h-[50vh] pt-16">
              <div className="flex flex-col gap-6 lg:grid grid-rows-1 grid-cols-1 lg:grid-cols-12 h-full p-9">
                <h2
                  id="services-heading"
                  className="text-white text-[9vw] lg:text-7xl font-urbanist font-black uppercase tracking-tight col-start-1"
                >
                  Servicios
                </h2>
                <p className="text-white text-[4vw] lg:text-xl place-self-start-safe font-urbanist font-light uppercase tracking-normal col-start-1 col-end-4">
                  Enfocamos nuestro trabajo con la máxima atención al detalle.
                  Póngase en contacto con nosotros para saber cómo podemos
                  ayudarle.
                </p>
                <p className="text-white text-[4vw] lg:text-xl place-self-end-safe font-urbanist font-light uppercase tracking-normal text-right col-start-10 col-end-13">
                  Ofrecemos una amplia gama de servicios creativos, centrados en
                  la producción cinematográfica de alta calidad.
                </p>
              </div>
            </header>

            {/* Lista de servicios */}
            <div className="text-white mx-auto w-full mt-12 mb-24">
              {services.map((service) => {
                return (
                  <Service
                    key={service.src}
                    title1={service.title1}
                    title2={service.title2}
                    src={service.src}
                    textClassName="font-urbanist font-black uppercase text-[6vw] lg:text-[5vw] leading-[1.2]"
                    secondTitleClassName="font-ibm-plex-serif font-normal italic"
                    wrapperClassName="w-fit mx-auto px-9 py-0 my-5 lg:my-0"
                    imgWrapperClassName="w-auto h-[8vh] aspect-video bg-cover object-cover"
                  />
                );
              })}
            </div>
          </section>

          {/* Sección promocional/decorativa */}
          <aside
            className="h-screen relative bg-cover bg-center border-b border-white/20"
            style={{
              backgroundImage: `url(/projects/viejito/img1.jpg)`,
            }}
            aria-label="Proyecto destacado Viejito"
          >
            <header className="absolute top-0 left-0 text-white font-urbanist font-black m-9 tracking-none uppercase">
              <h3 className="text-[clamp(2.5rem,12vw,6.5rem)] m-0 leading-none">
                Viejito
              </h3>
              <p className="m-0">Cliente: Ghost Cvlt</p>
              <p className="m-0">Proyecto: Viejito</p>
              <p className="m-0">Año: 2025</p>
            </header>
          </aside>

          {/* Separador visual para el efecto sticky footer */}
          <div className="w-full h-[1px] bg-black border-t border-white/20"></div>

          <Footer />
        </main>
      )}
    </div>
  );
};

export default App;
